<template>
	<div class="trade-panel" :class="{ 'is-compact': compact }">
		<div class="trade-panel__section">
			<div class="trade-panel__label">选择品种</div>
			<div class="trade-panel__symbols">
				<button
					v-for="symbol in symbols"
					:key="symbol.code"
					class="trade-panel__symbol-btn"
					:class="{ 'is-active': symbol.code === selectedCode }"
					@click="$emit('select-symbol', symbol.code)"
				>
					{{ symbol.code }}
				</button>
			</div>
		</div>

		<div class="trade-panel__section trade-panel__section--inline">
			<div class="trade-panel__label">杠杆倍数</div>
			<div class="trade-panel__leverages">
				<button
					v-for="level in leverages"
					:key="level"
					class="trade-panel__lev-btn"
					:class="{ 'is-active': level === leverage }"
					@click="$emit('select-leverage', level)"
				>
					{{ level }}×
				</button>
			</div>
		</div>

		<div class="trade-panel__section">
			<div class="trade-panel__label-row">
				<span class="trade-panel__label">投入金额</span>
				<span class="trade-panel__amount">{{ fmtMoney(marginDollars * 100) }}</span>
			</div>
			<input
				v-model.number="marginDollars"
				type="range"
				class="trade-panel__range"
				min="0"
				:max="maxDollars"
				step="50"
				:disabled="disabled"
				:style="rangeStyle"
				@input="onInput"
			/>
			<div class="trade-panel__meta">
				<span>名义仓位 ≈ {{ fmtMoney(marginCents * leverage) }}</span>
				<span>手续费 {{ fmtMoney(feeCents) }}</span>
			</div>
			<div class="trade-panel__scale">
				<span>最多 {{ fmtMoney(maxDollars * 100) }}</span>
				<span>可承受反向波动 {{ fmtPercent(1 / leverage) }}</span>
			</div>
		</div>

		<div class="trade-panel__actions">
			<button class="trade-panel__btn trade-panel__btn--long" :disabled="disabled" @click="$emit('open', 'long')">
				<span class="trade-panel__btn-glyph">▲</span>做多 / 买入
			</button>
			<button class="trade-panel__btn trade-panel__btn--short" :disabled="disabled" @click="$emit('open', 'short')">
				<span class="trade-panel__btn-glyph">▼</span>做空 / 卖出
			</button>
		</div>
	</div>
</template>

<script>
import { fmtMoney, fmtPercent } from '@/util/fx/format'

export default {
	name: 'TradePanel',
	props: {
		symbols: {
			type: Array,
			default: () => []
		},
		selectedCode: {
			type: String,
			default: ''
		},
		leverages: {
			type: Array,
			default: () => []
		},
		leverage: {
			type: Number,
			default: 25
		},
		disabled: {
			type: Boolean,
			default: false
		},
		balanceCents: {
			type: Number,
			default: 0
		},
		feeCents: {
			type: Number,
			default: 0
		},
		compact: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			marginDollars: 1000
		}
	},
	computed: {
		/**
		 * 滑块上限（美元）：扣除手续费后的可用余额
		 */
		maxDollars() {
			return Math.max(0, Math.floor((this.balanceCents - this.feeCents) / 100))
		},
		/**
		 * 投入金额（分）
		 */
		marginCents() {
			return Math.round((Number(this.marginDollars) || 0) * 100)
		},
		/**
		 * 滑块轨道填充样式：已选进度为强调色，剩余为暗色
		 */
		rangeStyle() {
			const max = this.maxDollars
			const ratio = max > 0 ? Math.min(1, (Number(this.marginDollars) || 0) / max) : 0
			const percent = (ratio * 100).toFixed(2) + '%'
			return {
				background: 'linear-gradient(90deg, var(--fx-accent) ' + percent + ', rgba(148, 170, 220, 0.22) ' + percent + ')'
			}
		}
	},
	watch: {
		maxDollars: {
			immediate: true,
			handler(value) {
				if (this.marginDollars > value) {
					this.marginDollars = value
					this.$emit('update-margin', this.marginCents)
				}
			}
		}
	},
	methods: {
		fmtMoney,
		fmtPercent,
		/**
		 * 输入变化时上抛金额（分）
		 */
		onInput() {
			this.$emit('update-margin', this.marginCents)
		}
	}
}
</script>

<style scoped>
.trade-panel {
	display: flex;
	flex-direction: column;
	gap: 8px;
	background: linear-gradient(180deg, var(--fx-panel-2), var(--fx-panel));
	border: 1px solid var(--fx-border);
	border-top: 2px solid rgba(77, 162, 255, 0.55);
	border-radius: 10px;
	padding: 10px 12px;
}

.trade-panel__section {
	display: flex;
	flex-direction: column;
	gap: 5px;
	min-width: 0;
}

.trade-panel__section--inline {
	flex-direction: row;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
}

.trade-panel__label {
	font-size: 12.5px;
	color: var(--fx-text-dim);
	letter-spacing: 0.04em;
	white-space: nowrap;
}

.trade-panel__symbols {
	display: flex;
	flex-wrap: wrap;
	gap: 5px;
}

.trade-panel__symbol-btn {
	padding: 4px 9px;
	border-radius: 7px;
	border: 1px solid var(--fx-border);
	background: rgba(255, 255, 255, 0.04);
	color: var(--fx-text-dim);
	font-size: 13px;
	cursor: pointer;
	transition: all 0.15s;
}

.trade-panel__symbol-btn:hover {
	border-color: var(--fx-border-strong);
	color: var(--fx-text);
	transform: translateY(-1px);
}

.trade-panel__symbol-btn.is-active {
	border-color: rgba(77, 162, 255, 0.55);
	background: var(--fx-accent-soft);
	color: #9cc7ff;
	box-shadow: 0 0 10px rgba(77, 162, 255, 0.18);
}

.trade-panel__leverages {
	display: flex;
	flex-wrap: wrap;
	gap: 3px;
	padding: 3px;
	border-radius: 9px;
	border: 1px solid var(--fx-border);
	background: rgba(255, 255, 255, 0.03);
}

.trade-panel__lev-btn {
	padding: 3px 8px;
	border-radius: 6px;
	border: none;
	background: transparent;
	color: var(--fx-text-dim);
	font-size: 13px;
	font-family: var(--fx-mono);
	cursor: pointer;
	transition: all 0.15s;
}

.trade-panel__lev-btn:hover {
	color: var(--fx-text);
}

.trade-panel__lev-btn.is-active {
	background: var(--fx-accent-soft);
	color: #9cc7ff;
	box-shadow: inset 0 0 0 1px rgba(77, 162, 255, 0.5);
}

.trade-panel__label-row {
	display: flex;
	align-items: baseline;
	justify-content: space-between;
}

.trade-panel__label-row .trade-panel__label {
	font-size: 12.5px;
	color: var(--fx-text-dim);
}

.trade-panel__amount {
	font-size: 18px;
	font-weight: 700;
	font-family: var(--fx-mono);
	font-variant-numeric: tabular-nums;
	color: var(--fx-text);
}

.trade-panel__range {
	width: 100%;
	height: 5px;
	margin: 4px 0 0;
	-webkit-appearance: none;
	appearance: none;
	border-radius: 999px;
	background: rgba(148, 170, 220, 0.22);
	outline: none;
	cursor: pointer;
}

.trade-panel__range::-webkit-slider-thumb {
	-webkit-appearance: none;
	appearance: none;
	width: 15px;
	height: 15px;
	border-radius: 50%;
	background: var(--fx-accent);
	border: 2px solid var(--fx-panel-2);
	box-sizing: content-box;
	box-shadow: 0 0 0 3px rgba(77, 162, 255, 0.18);
	transition: box-shadow 0.15s;
}

.trade-panel__range:hover::-webkit-slider-thumb,
.trade-panel__range:active::-webkit-slider-thumb {
	box-shadow: 0 0 0 5px rgba(77, 162, 255, 0.28);
}

.trade-panel__range::-moz-range-thumb {
	width: 13px;
	height: 13px;
	border-radius: 50%;
	background: var(--fx-accent);
	border: 2px solid var(--fx-panel-2);
	box-shadow: 0 0 0 3px rgba(77, 162, 255, 0.18);
}

.trade-panel__range:disabled {
	opacity: 0.4;
	cursor: not-allowed;
}

.trade-panel__meta {
	display: flex;
	justify-content: space-between;
	gap: 8px;
	font-size: 12px;
	color: var(--fx-text-dim);
	font-family: var(--fx-mono);
	white-space: nowrap;
}

.trade-panel__scale {
	display: flex;
	justify-content: space-between;
	gap: 8px;
	font-size: 12px;
	color: var(--fx-text-faint);
	font-family: var(--fx-mono);
	white-space: nowrap;
}

.trade-panel__actions {
	display: flex;
	gap: 8px;
}

.trade-panel__btn {
	display: flex;
	align-items: center;
	justify-content: center;
	flex: 1;
	padding: 9px 0;
	border: none;
	border-radius: 9px;
	font-size: 14px;
	font-weight: 700;
	letter-spacing: 0.02em;
	cursor: pointer;
	color: #fff;
	transition: transform 0.15s, box-shadow 0.2s, filter 0.15s;
}

.trade-panel__btn:disabled {
	opacity: 0.38;
	cursor: not-allowed;
}

.trade-panel__btn-glyph {
	font-size: 12px;
	margin-right: 5px;
}

.trade-panel__btn--long {
	background: linear-gradient(180deg, #ff6f63, #e84a3d);
	box-shadow: 0 4px 14px rgba(245, 86, 74, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.18);
}

.trade-panel__btn--long:hover:not(:disabled) {
	transform: translateY(-1px);
	filter: brightness(1.05);
	box-shadow: 0 8px 22px rgba(245, 86, 74, 0.42), inset 0 1px 0 rgba(255, 255, 255, 0.18);
}

.trade-panel__btn--long:active:not(:disabled) {
	transform: translateY(0);
}

.trade-panel__btn--short {
	background: linear-gradient(180deg, #3bd698, #1fa876);
	box-shadow: 0 4px 14px rgba(46, 189, 133, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.16);
}

.trade-panel__btn--short:hover:not(:disabled) {
	transform: translateY(-1px);
	filter: brightness(1.05);
	box-shadow: 0 8px 22px rgba(46, 189, 133, 0.42), inset 0 1px 0 rgba(255, 255, 255, 0.16);
}

.trade-panel__btn--short:active:not(:disabled) {
	transform: translateY(0);
}

/* 面板被拖窄时的紧凑布局：缩小按钮与字号，保证杠杆行不换行 */
.trade-panel.is-compact {
	padding: 7px 9px;
	gap: 6px;
}

.trade-panel.is-compact .trade-panel__section--inline {
	gap: 5px;
}

.trade-panel.is-compact .trade-panel__label {
	font-size: 11.5px;
}

.trade-panel.is-compact .trade-panel__symbols {
	gap: 4px;
}

.trade-panel.is-compact .trade-panel__leverages {
	gap: 2px;
	padding: 2px;
}

.trade-panel.is-compact .trade-panel__symbol-btn {
	padding: 3px 7px;
	font-size: 12px;
}

.trade-panel.is-compact .trade-panel__lev-btn {
	padding: 2px 6px;
	font-size: 12px;
}

.trade-panel.is-compact .trade-panel__amount {
	font-size: 15px;
}

.trade-panel.is-compact .trade-panel__meta,
.trade-panel.is-compact .trade-panel__scale {
	font-size: 10.5px;
	gap: 4px;
}

.trade-panel.is-compact .trade-panel__btn {
	padding: 7px 0;
	font-size: 13px;
}
</style>
